import type { ReactElement } from 'react';
import React, { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { keyBy, mapValues } from 'lodash';
import type { Column, IdType, TableState, UseTableOptions } from 'react-table';
import {
  useFilters,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import type { PlaceHolderProps } from 'atom-components/table-outdated/no-data-placeholder/no-data-placeholder';
import NoDataPlaceholder from 'atom-components/table-outdated/no-data-placeholder/no-data-placeholder';
import NoResultsPlaceholder from 'atom-components/table-outdated/no-results-placeholder/no-results-placeholder';
import TablePagination from 'atom-components/table-outdated/pagination/pagination';
import { useRowSelectColumn } from 'atom-components/table-outdated/row-select/row-select';
import type { Filters, Sort } from './types';
import './table.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TableDatum = any;

type Props = {
  columns: readonly Column<TableDatum>[];
  data: readonly TableDatum[];
  pageSize?: number;
  hiddenColumns?: TableState['hiddenColumns'];
  filterBy?: Filters;
  onFilter?: (arg: Filters) => void;
  sortBy?: Sort;
  onSort?: (arg: Sort) => void;
  onSelectRow?(ids: IdType<number>[]): void;
  getRowId?: UseTableOptions<{ id: string; classExternalId: string }>['getRowId'];
  className?: string;
  initialSelectedRowIds?: (string | number)[];
};

function Table({
  columns,
  data,
  getRowId,
  onSort,
  onFilter,
  pageSize,
  hiddenColumns,
  filterBy,
  sortBy: initialSortBy,
  initialSelectedRowIds = [],
  onSelectRow,
  className
}: Props): ReactElement | null {
  const initialState = useMemo(
    () => ({
      pageSize: pageSize || data?.length,
      hiddenColumns: hiddenColumns || [],
      filters: filterBy || [],
      sortBy: initialSortBy || [],
      selectedRowIds: mapValues(keyBy(initialSelectedRowIds), (v) => !!v)
    }),
    [pageSize, data?.length, hiddenColumns, filterBy, initialSortBy, initialSelectedRowIds]
  );

  const selectColumn = useRowSelectColumn(!!onSelectRow);
  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: () => null
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageCount,
    canNextPage,
    canPreviousPage,
    previousPage,
    nextPage,
    gotoPage,
    setHiddenColumns,
    state: { pageIndex, sortBy, filters, selectedRowIds }
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState,
      autoResetFilters: false,
      autoResetSortBy: false,
      getRowId
    },
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((visibleColumns) => [...selectColumn, ...visibleColumns]);
    }
  );

  useEffect(() => {
    if (onSort) {
      onSort(sortBy);
    }
  }, [onSort, sortBy]);

  useEffect(() => {
    if (onFilter) {
      onFilter(filters);
    }
  }, [onFilter, filters]);

  useEffect(() => {
    if (onSelectRow) {
      onSelectRow(Object.keys(selectedRowIds).filter((k) => selectedRowIds[k]));
    }
  }, [onSelectRow, selectedRowIds]);

  // Set new hidden columns if they change
  useEffect(() => {
    setHiddenColumns(hiddenColumns || []);
  }, [hiddenColumns, setHiddenColumns]);

  const hasFilters = useMemo(
    () => !!headerGroups.find((h) => !!h.headers.find((c) => !!c.Filter)),
    [headerGroups]
  );
  if (!data?.length) {
    return null;
  }

  return (
    <div className="table-responsive mobile-table">
      <table
        {...getTableProps()}
        className={clsx(className, 'nzcer-table table', { 'has-filters': hasFilters })}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroup.id}`}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  className={clsx(
                    column.getHeaderProps().className,
                    column.className,
                    column.headerClassName
                  )}
                  key={`header-${column.id}`}
                >
                  <div
                    className={clsx('table-heading', column.headingClassName)}
                    {...column.getSortByToggleProps()}
                  >
                    <span className="heading-text">{column.render('Header')}</span>
                    {column.canSort && (
                      <span className="sorting">
                        <FontAwesomeIcon
                          icon="sort-up"
                          className={column.isSorted && !column.isSortedDesc ? 'invisible' : ''}
                        />
                        <FontAwesomeIcon
                          icon="sort-down"
                          className={column.isSorted && column.isSortedDesc ? 'invisible' : ''}
                        />
                      </span>
                    )}
                  </div>
                  {column.canFilter && (
                    <div className="heading-filter">{column.render('Filter')}</div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {!page?.length ? (
            <NoResultsPlaceholder />
          ) : (
            page.map((row) => {
              prepareRow(row);

              return (
                <tr {...row.getRowProps()} key={`row-${row.id}`}>
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={`cell-${cell.column.id}-${cell.row.id}`}
                      className={clsx(
                        cell.column.getHeaderProps().className,
                        cell.column.className,
                        cell.column.cellClassName,
                        'cell'
                      )}
                    >
                      <div className="cell-header h-100 d-none">{cell.render('Header')}</div>
                      <div
                        className={clsx(cell.column.cellClassName, 'cell-content h-100 border-0')}
                      >
                        {cell.render('Cell')}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pageCount > 1 && (
        <TablePagination
          pageCount={pageCount}
          pageIndex={pageIndex}
          nextPage={nextPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          gotoPage={gotoPage}
        />
      )}
    </div>
  );
}

type TableWrapperProps = Props & PlaceHolderProps;

export default function TableWrapper({ placeholder, ...props }: TableWrapperProps): ReactElement {
  if (!props.data?.length) {
    return <NoDataPlaceholder placeholder={placeholder} />;
  }

  return <Table {...props} />;
}
